import { useCallback, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { notify } from 'messages';

import api from 'services/api';
import { RootState } from 'redux/store';
import { formatTimestamp, getLocalTimezoneLabel } from 'helpers/formatDateToLocalTimezone';
import { selectHour12Option } from 'redux/selectors/userSettings';
import ConfirmationModal from 'components/common/ConfirmationModal';

interface NodeInstanceNote {
  node_instance_note_id: number;
  node_instance_id: number;
  note: string;
  username: string;
  created_at: string;
  updated_at: string;
}

interface NodeInstanceNotesProps {
  nodeInstanceId?: number | string | null;
}

const getErrorMessage = (error: any, fallback: string) => error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

/**
 * Announces that an instance gained or lost a note, so lists showing the
 * `has_note` flag can invalidate the row. Notes live in this component's local
 * state, not in redux, so there is nothing for a list to subscribe to — the
 * node-instances page listens for this the same way it listens for
 * `operator-message-responded`.
 *
 * Editing a note cannot change `has_note`, so only create and delete announce.
 */
export const NODE_INSTANCE_NOTES_CHANGED = 'node-instance-notes-changed';

const announceNotesChanged = () => window.dispatchEvent(new CustomEvent(NODE_INSTANCE_NOTES_CHANGED));

const NodeInstanceNotes = ({ nodeInstanceId }: NodeInstanceNotesProps) => {
  const [notes, setNotes] = useState<NodeInstanceNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);
  const [deleteTargetNote, setDeleteTargetNote] = useState<NodeInstanceNote | null>(null);

  const userData = useSelector((state: RootState) => state.auth.userData);
  const localTimezone = userData?.user_settings?.useLocalTimezone === true;
  const localTimezoneLabel = getLocalTimezoneLabel();
  const hour12 = useSelector(selectHour12Option);

  const loadNotes = useCallback(async () => {
    if (!nodeInstanceId) {
      setNotes([]);
      setLoadError('');
      return;
    }

    setNotes([]);
    setLoading(true);
    setLoadError('');

    try {
      const response = await api.get(`/node_instances/${nodeInstanceId}/notes`);
      setNotes(response.data?.notes || []);
    } catch (error: any) {
      setLoadError(getErrorMessage(error, 'Failed to load notes'));
    } finally {
      setLoading(false);
    }
  }, [nodeInstanceId]);

  useEffect(() => {
    setEditingNoteId(null);
    setEditingDraft('');
    setDraft('');
    loadNotes();
  }, [loadNotes]);

  const formatNoteTimestamp = (value?: string | null) => {
    if (!value) return '';
    const formatted = formatTimestamp(value, {
      localTimezone,
      hour12,
      fallbackTimezone: 'UTC',
      invalidValue: typeof value === 'string' ? value : '',
    });

    return formatted ? `${formatted} (${localTimezone ? localTimezoneLabel : 'UTC'})` : '';
  };

  const handleCreate = async () => {
    const note = draft.trim();
    if (!note || !nodeInstanceId) {
      notify.error('Please enter a note before saving.');
      return;
    }

    setSaving(true);

    try {
      const response = await api.post(`/node_instances/${nodeInstanceId}/notes`, {
        note,
      });
      setNotes((prev) => [...prev, response.data]);
      setDraft('');
      announceNotesChanged();
      notify.success('Note added.');
    } catch (error: any) {
      notify.error(getErrorMessage(error, 'Failed to add note'));
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (note: NodeInstanceNote) => {
    setEditingNoteId(note.node_instance_note_id);
    setEditingDraft(note.note);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingDraft('');
  };

  const handleSaveEdit = async (noteId: number) => {
    const nextNote = editingDraft.trim();
    if (!nextNote || !nodeInstanceId) {
      notify.error('Please enter a note before saving.');
      return;
    }

    setSaving(true);

    try {
      const response = await api.put(`/node_instances/${nodeInstanceId}/notes/${noteId}`, { note: nextNote });

      setNotes((prev) => prev.map((note) => (note.node_instance_note_id === noteId ? response.data : note)));
      setEditingNoteId(null);
      setEditingDraft('');
      notify.success('Note updated.');
    } catch (error: any) {
      notify.error(getErrorMessage(error, 'Failed to update note'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!nodeInstanceId || !deleteTargetNote) return;

    const noteId = deleteTargetNote.node_instance_note_id;
    setDeletingNoteId(noteId);

    try {
      await api.delete(`/node_instances/${nodeInstanceId}/notes/${noteId}`);
      setNotes((prev) => prev.filter((note) => note.node_instance_note_id !== noteId));
      if (editingNoteId === noteId) {
        setEditingNoteId(null);
        setEditingDraft('');
      }
      setDeleteTargetNote(null);
      announceNotesChanged();
      notify.success('Note deleted.');
    } catch (error: any) {
      notify.error(getErrorMessage(error, 'Failed to delete note'));
    } finally {
      setDeletingNoteId(null);
    }
  };

  if (!nodeInstanceId) {
    return null;
  }

  return (
    <>
      <ConfirmationModal
        show={deleteTargetNote !== null}
        title="Delete note"
        body="Are you sure you want to delete this note?"
        confirmText={deletingNoteId !== null ? 'Deleting...' : 'Delete'}
        confirmVariant="danger"
        disableConfirm={deletingNoteId !== null}
        onCancel={() => {
          if (deletingNoteId !== null) return;
          setDeleteTargetNote(null);
        }}
        onConfirm={handleDelete}
      />

      <Form.Group className="mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <Form.Label className="mb-0">Notes</Form.Label>
          {loading && <Spinner animation="border" size="sm" />}
        </div>

        <Form.Control
          as="textarea"
          rows={3}
          className="mb-2"
          placeholder="Add a note for this node instance"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={saving}
        />

        <div className="d-flex justify-content-end mb-3">
          <Button onClick={handleCreate} disabled={saving}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {saving && editingNoteId === null ? 'Saving...' : 'Add note'}
          </Button>
        </div>

        {loadError && <small className="text-danger d-block mb-2">{loadError}</small>}

        {!loading && notes.length === 0 && <div className="border rounded-2 p-3 text-muted">No notes have been added yet.</div>}

        {notes.map((note) => {
          const isEditing = editingNoteId === note.node_instance_note_id;
          const showEdited = !!note.updated_at && !!note.created_at && note.updated_at !== note.created_at;

          return (
            <div key={note.node_instance_note_id} className="border rounded-2 p-3 mb-2">
              <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                <div>
                  <div className="fw-semibold">{note.username || 'Unknown user'}</div>
                  <div className="text-muted fs-10">
                    {formatNoteTimestamp(note.created_at)}
                    {showEdited ? ` • edited ${formatNoteTimestamp(note.updated_at)}` : ''}
                  </div>
                </div>

                <div className="d-flex gap-2">
                  {!isEditing && (
                    <Button size="sm" variant="outline-secondary" onClick={() => handleStartEdit(note)}>
                      <FontAwesomeIcon icon={faPen} className="me-1" />
                      Edit
                    </Button>
                  )}
                  <Button size="sm" variant="outline-danger" onClick={() => setDeleteTargetNote(note)} disabled={deletingNoteId === note.node_instance_note_id}>
                    <FontAwesomeIcon icon={faTrash} className="me-1" />
                    {deletingNoteId === note.node_instance_note_id ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>

              {isEditing ? (
                <>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    className="mb-2"
                    value={editingDraft}
                    onChange={(e) => setEditingDraft(e.target.value)}
                    disabled={saving}
                  />
                  <div className="d-flex justify-content-end gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={handleCancelEdit} disabled={saving}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => handleSaveEdit(note.node_instance_note_id)} disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </>
              ) : (
                <div style={{ whiteSpace: 'pre-wrap' }}>{note.note}</div>
              )}
            </div>
          );
        })}
      </Form.Group>
    </>
  );
};

export default NodeInstanceNotes;
